import React, { useState, useMemo } from 'react';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './LegalPages.module.css';

const FAQPage = () => {
  const [openSections, setOpenSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqData = [
    {
      id: 'getting-started',
      title: 'Démarrage',
      icon: '🚀',
      questions: [
        {
          question: 'Comment créer un compte sur la marketplace domotique ?',
          answer: 'Cliquez sur "S\'inscrire" en haut à droite. Vous pouvez vous inscrire avec votre adresse email ou via Google/Facebook pour une inscription rapide. Après validation de votre email, vous pourrez acheter ou vendre des équipements domotiques. Vous devez être majeur pour créer un compte et effectuer des achats.'
        },
        {
          question: 'L\'utilisation de la marketplace est-elle gratuite ?',
          answer: 'Oui ! La création de compte et la navigation sont entièrement gratuites.'
        },
        {
          question: 'Comment rechercher des équipements domotiques spécifiques ?',
          answer: 'Utilisez la barre de recherche en haut de chaque page. Recherchez par nom de produit, marque (Philips Hue, Sonoff, Shelly, etc.), protocole (Zigbee, Z-Wave, WiFi, Matter), ou catégorie (éclairage, chauffage, sécurité). Les filtres avancés sur la pages des résultats permettent d\'affiner par prix, compatibilité, protocole de communication, et état du produit.'
        }
      ]
    },
    {
      id: 'compatibility',
      title: 'Compatibilité & Protocoles',
      icon: '🔌',
      questions: [
        {
          question: 'Comment savoir si un appareil est compatible avec mon système domotique ?',
          answer: 'Chaque fiche produit indique les protocoles supportés (Zigbee, Z-Wave, WiFi, Thread, Matter, KNX) et les plateformes compatibles (Home Assistant, Amazon Alexa, Google Home, Apple HomeKit, ...). Vérifiez la section "Spécifications techniques". En cas de doute, contactez notre support client.'
        },
        {
          question: 'Que signifient les différents protocoles (Zigbee, Z-Wave, WiFi, Matter) ?',
          answer: 'Zigbee et Z-Wave sont des protocoles mesh basse consommation nécessitant un hub/gateway. WiFi se connecte directement à votre réseau sans hub mais consomme plus d\'énergie. Matter est le nouveau standard universel garantissant l\'interopérabilité entre marques. Thread combine les avantages du mesh et de l\'efficacité énergétique. Consultez notre guide technique pour plus de détails.'
        },
        {
          question: 'Puis-je intégrer des appareils de différents fabricants ?',
          answer: 'Absolument ! C\'est l\'avantage des systèmes ouverts. Avec un hub compatible (Home Assistant, Jeedom, etc.), vous pouvez mixer Philips Hue, Sonoff, Shelly, Xiaomi, et autres marques. Les appareils certifiés Matter offrent une compatibilité garantie. Attention aux écosystèmes fermés propriétaires qui limitent l\'interopérabilité.'
        },
        {
          question: 'Qu\'est-ce qu\'un hub/gateway domotique et en ai-je besoin ?',
          answer: 'Un hub centralise la communication entre vos appareils et votre système domotique. Nécessaire pour Zigbee, Z-Wave et certains protocoles propriétaires. Les appareils WiFi et certains Matter peuvent fonctionner sans hub. Les hubs populaires incluent : ConBee II, Zigbee2MQTT, Z-Wave JS UI, ou hubs intégrés (Home Assistant, Jeedom, Eedomus).'
        }
      ]
    },
    {
      id: 'buying',
      title: 'Achat & Commande',
      icon: '🛒',
      questions: [
        {
          question: 'Comment passer une commande d\'équipement domotique ?',
          answer: 'Parcourez les annonces, ajoutez les produits au panier, puis procédez au paiement. Renseignez vos informations de livraison et paiement. Le prix final inclut la TVA et les frais de port. Après validation, vous recevrez une confirmation de commande puis lorsque le vendeur aura expédié la commande vous recevrez par email avec les détails du suivi de la livraison.'
        },
        {
          question: 'Quels moyens de paiement sont acceptés ?',
          answer: 'Nous acceptons les cartes bancaires (Visa, Mastercard, American Express). Tous les paiements sont sécurisés via Stripe avec chiffrement SSL/TLS. Vos informations bancaires ne sont jamais stockées sur nos serveurs.'
        },
        {
          question: 'Puis-je annuler ma commande d\'équipement domotique ?',
          answer: 'Les commandes peuvent être annulées selon les conditions définis par la politique du vendeur. Consultez la page de détails de commande pour les options d\'annulation ou contactez le support.'
        },
        {
          question: 'Comment suivre ma commande ?',
          answer: 'Une fois expédié, vous recevrez un numéro de suivi par email. Consultez également le statut dans votre tableau de bord sous "Mes Commandes". Le suivi peut prendre 24-48h pour s\'actualiser après l\'expédition. Les vendeurs choisissent le transporteur (Colissimo, Chronopost, Mondial Relay, UPS) et fournissent le suivi disponible.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Configuration & Installation',
      icon: '⚙️',
      questions: [
        {
          question: 'Les appareils sont-ils pré-configurés ou dois-je les installer moi-même ?',
          answer: 'La plupart des appareils sont vendus neufs ou réinitialisés aux paramètres d\'usine. L\'installation et configuration initiale sont à votre charge. Chaque vendeur indique l\'état (neuf, reconditionné, pré-configuré). Des guides d\'installation sont souvent fournis. Certains vendeurs proposent une assistance technique ou installation sur-mesure moyennant supplément.'
        },
        {
          question: 'Où puis-je trouver de l\'aide pour configurer mes appareils ?',
          answer: 'Consultez la documentation fournie avec le produit, notre base de connaissances technique, ou les forums communautaires (Home Assistant Community, Jeedom Forum). Les vendeurs professionnels offrent souvent un support technique de base. Pour une assistance avancée, des intégrateurs certifiés sont disponibles sur notre marketplace de services.'
        },
        {
          question: 'Que faire si mon appareil ne fonctionne pas après l\'installation ?',
          answer: 'Vérifiez d\'abord : connexion réseau/hub, portée du signal, version firmware à jour, compatibilité protocole. Consultez le guide de dépannage fourni. Si le problème persiste, contactez le vendeur via la messagerie de commande avec : modèle exact, système utilisé, message d\'erreur, captures d\'écran. Le vendeur ou notre support technique vous assisteront.'
        }
      ]
    },
    {
      id: 'shipping',
      title: 'Livraison',
      icon: '📦',
      questions: [
        {
          question: 'Quels sont les délais de livraison ?',
          answer: 'Les délais varient selon le vendeur et votre localisation. Généralement : expédition sous 1-3 jours ouvrés, livraison sous 3-7 jours ouvrés en France métropolitaine. Les options express sont disponibles au moment du paiement. Les équipements volumineux ou systèmes complets peuvent nécessiter un délai supplémentaire. Les délais estimés sont affichés sur chaque annonce.'
        },
        {
          question: 'Que faire si mon colis est endommagé ?',
          answer: 'Si le colis arrive endommagé, prenez immédiatement des photos (emballage + contenu) et contactez le support sous 48h. Nous travaillerons avec le vendeur pour résoudre le problème : remplacement, réparation, ou remboursement. Pour les équipements électroniques sensibles, n\'acceptez pas un colis visiblement endommagé et signalez-le au transporteur.'
        }
      ]
    },
    {
      id: 'returns',
      title: 'Retours & Remboursements',
      icon: '↩️',
      questions: [
        {
          question: 'Quelle est votre politique de retour ?',
          answer: 'La plupart des articles peuvent être retournés sous 14 jours après livraison, dans leur état d\'origine avec emballage et accessoires complets (câbles, adaptateurs, documentation). Les équipements configurés sur-mesure, modules flashés avec firmware personnalisé, ou produits d\'hygiène peuvent être non retournables. Consultez l\'annonce pour les conditions spécifiques de retour.'
        },
        {
          question: 'Comment initier un retour ?',
          answer: 'Accédez à "Mes Commandes", sélectionnez la commande concernée, et contactez le vendeur via la conversation. Indiquez : numéro de commande, motif de retour, preuves (photos si défaut). Suivez les instructions d\'emballage et d\'expédition si le retour est autorisé. Sauf indication contraire, les frais de retour sont à votre charge.'
        },
        {
          question: 'Combien de temps prend le remboursement ?',
          answer: 'Une fois le retour reçu et vérifié, les remboursements sont traités sous 5-10 jours ouvrés vers votre moyen de paiement original. Le délai d\'apparition sur votre compte dépend de votre banque ou prestataire de paiement. Les remboursements suivent notre Politique de Retour et peuvent prendre 5-10 jours supplémentaires pour apparaître sur votre relevé.'
        },
        {
          question: 'Que faire si j\'ai reçu le mauvais article ?',
          answer: 'Contactez immédiatement le support si vous recevez un article incorrect. Nous organiserons l\'envoi du bon produit et fournirons une étiquette de retour prépayée pour l\'article erroné. Si l\'article est défectueux ou incorrect, le vendeur couvrira les frais de retour. Fournissez photos et détails pour une résolution rapide.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Compte & Sécurité',
      icon: '🔐',
      questions: [
        {
          question: 'J\'ai oublié mon mot de passe, comment le réinitialiser ?',
          answer: 'Cliquez sur "Mot de passe oublié" sur la page de connexion et saisissez votre email. Vous recevrez un lien de réinitialisation par email. Cliquez sur le lien pour créer un nouveau mot de passe. Conservez vos identifiants en sécurité et activez l\'authentification multi-facteurs si disponible. Vous êtes responsable de toute activité sur votre compte.'
        },
        {
          question: 'Comment mettre à jour mes informations de compte ?',
          answer: 'Accédez à "Paramètres du Compte" dans votre menu profil. Vous pouvez modifier : informations personnelles, adresses de livraison, moyens de paiement, préférences de notification. Fournissez des informations exactes et à jour. Ne partagez pas votre compte et n\'utilisez pas de fausse identité. Nous pouvons suspendre ou résilier votre compte en cas de violation des règles.'
        },
        {
          question: 'Mes informations personnelles sont-elles sécurisées ?',
          answer: 'Absolument ! Nous utilisons le chiffrement standard de l\'industrie (SSL/TLS 256-bit) et des mesures de sécurité robustes pour protéger vos données. Nous ne partageons jamais vos informations personnelles avec des tiers, sauf pour exécuter les commandes ou respecter les obligations légales. Vos données peuvent être traitées hors de votre pays, mais nous garantissons des protections adéquates.'
        },
        {
          question: 'Comment supprimer mon compte ?',
          answer: 'Contactez le support pour demander la suppression de compte. Cette action supprimera définitivement toutes vos données : historique de commandes, informations enregistrées, préférences. Les commandes en cours doivent être finalisées avant suppression. Nous conservons certaines informations conformément à notre Politique de Confidentialité et aux obligations légales (comptabilité, contentieux).'
        }
      ]
    },
    {
      id: 'support',
      title: 'Support Client',
      icon: '🆘',
      questions: [
        {
          question: 'Comment contacter le support client ?',
          answer: 'Nous proposons plusieurs canaux de support : chat en direct (24/7), email à contact@neokasa.fr, et support téléphonique aux heures ouvrables. Consultez également notre Centre d\'Aide pour les questions courantes. Pour les questions légales : contact@neokasa.fr. Pour les demandes de confidentialité : contact@neokasa.fr. Notre équipe technique peut vous aider pour les questions de compatibilité domotique.'
        },
        {
          question: 'Quels sont vos horaires de support ?',
          answer: 'Chat en direct disponible 24/7 pour les urgences. Support email répond sous 4 heures durant les heures ouvrables (Lundi-Vendredi, 9h-18h CET). Support téléphonique : Lundi-Vendredi, 9h-18h CET. Pour les problèmes urgents avec commandes actives, utilisez le chat pour une assistance immédiate. Le support technique avancé (compatibilité, configuration) est disponible en semaine.'
        },
        {
          question: 'Puis-je parler à une vraie personne ?',
          answer: 'Oui ! Notre chat en direct vous connecte directement avec un conseiller du service client. Pour les problèmes complexes ou questions techniques approfondies, nous pouvons organiser des appels téléphoniques ou visioconférences si nécessaire. Nous sommes engagés à fournir un support personnalisé pour résoudre vos préoccupations rapidement et efficacement.'
        },
        {
          question: 'Proposez-vous un support technique pour la configuration ?',
          answer: 'Oui ! Notre équipe peut vous guider sur : choix d\'équipements compatibles, protocoles de communication, bases de configuration. Pour un support technique avancé (programmation d\'automatisations complexes, intégration multi-protocoles, dépannage système complet), consultez nos services professionnels ou partenaires intégrateurs certifiés disponibles sur la marketplace.'
        }
      ]
    },
    {
      id: 'legal',
      title: 'Légal & Conformité',
      icon: '⚖️',
      questions: [
        {
          question: 'Les équipements sont-ils conformes aux normes européennes ?',
          answer: 'Tous les équipements neufs vendus doivent être conformes aux directives CE applicables (RED pour radio, LVD pour basse tension, EMC pour compatibilité électromagnétique, RoHS pour substances). Les vendeurs doivent fournir les déclarations de conformité. Pour les équipements d\'occasion ou importés, vérifiez les certifications avec le vendeur. Les équipements non conformes sont strictement interdits.'
        },
        {
          question: 'Quels sont mes droits en matière de confidentialité ?',
          answer: 'Conformément au RGPD, vous pouvez accéder, rectifier ou supprimer vos données personnelles dans les paramètres ou en nous contactant. Vous disposez des droits de portabilité, limitation du traitement, et opposition. Pour exercer ces droits : contact@neokasa.fr. Nous ne vendons pas vos données personnelles. Consultez notre Politique de Confidentialité pour tous les détails.'
        }
      ]
    }
  ];

  // Filter FAQ data based on search query
  const filteredFaqData = useMemo(() => {
    let filtered = faqData;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.map(section => ({
        ...section,
        questions: section.questions.filter(q =>
          q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.questions.length > 0);
    }

    return filtered;
  }, [searchQuery]);

  // Generate Schema.org FAQPage structured data for SEO
  const faqSchema = useMemo(() => {
    const allQuestions = faqData.flatMap(section => 
      section.questions.map(q => ({
        '@type': 'Question',
        'name': q.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': q.answer
        }
      }))
    );

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': allQuestions
    };
  }, []);

  return (
    <Page 
      title="FAQ - Questions Fréquentes | Marketplace Domotique Neokasa" 
      description="Réponses complètes sur la domotique : compatibilité Zigbee, Z-Wave, Matter, installation d'équipements connectés, support technique, livraison et retours. Guide complet pour votre maison intelligente."
      scrollingDisabled={false}
    >
      {/* FAQ Schema for SEO - Injected directly to avoid @graph wrapping */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <div className={css.header}>
              <h1 className={css.title}>Questions Fréquentes</h1>
              <p className={css.subtitle}>Trouvez des réponses aux questions les plus courantes sur notre marketplace domotique</p>
              <p className={css.updated}>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>

            {/* Search */}
            <div className={css.navigationSection}>
              <div className={css.searchContainer}>
                <input
                  type="text"
                  placeholder="Rechercher une question..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={css.searchInput}
                />
                <span className={css.searchIcon}>🔍</span>
              </div>
            </div>

            {/* FAQ Results */}
            <div className={css.faqContainer}>
              {filteredFaqData.length === 0 ? (
                <div className={css.noResults}>
                  <div className={css.noResultsIcon}>🤔</div>
                  <h3 className={css.noResultsTitle}>Aucune question trouvée</h3>
                  <p className={css.noResultsText}>
                    Essayez d'ajuster vos termes de recherche pour trouver ce que vous cherchez.
                  </p>
                  <button
                    className={css.resetSearchButton}
                    onClick={() => {
                      setSearchQuery('');
                    }}
                  >
                    Réinitialiser la recherche
                  </button>
                </div>
              ) : (
                filteredFaqData.map((section) => (
                  <div key={section.id} className={css.faqSection}>
                    <button
                      className={`${css.sectionHeader} ${openSections[section.id] ? css.sectionHeaderOpen : ''}`}
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={openSections[section.id]}
                    >
                      <span className={css.sectionIcon}>{section.icon}</span>
                      <h2 className={css.sectionTitle}>{section.title}</h2>
                      <span className={css.expandIcon}>
                        {openSections[section.id] ? '−' : '+'}
                      </span>
                    </button>

                    {openSections[section.id] && (
                      <div className={css.sectionContent}>
                        {section.questions.map((item, index) => (
                          <div key={index} className={css.questionItem}>
                            <h3 className={css.question}>{item.question}</h3>
                            <div>
                              <p className={css.answer}>{item.answer}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className={css.contactSection}>
              <h2 className={css.contactTitle}>Vous avez encore des questions ?</h2>
              <p className={css.contactText}>
                Vous ne trouvez pas ce que vous cherchez ? Notre équipe de support client est là pour vous aider !
              </p>
              <div className={css.contactButtons}>
                <a href="/contact" className={css.contactButton}>
                  Contacter le Support
                </a>
                <a href="mailto:contact@neokasa.fr" className={css.contactButtonSecondary}>
                  Support par Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FAQPage;


